import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, message, Modal } from 'antd';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useCanCheck } from '@/ability/helpers';
import {
    PROJECT_ISSUES_AND_UPGRADES_VIEW,
    PROJECT_ISSUES_AND_UPGRADES_CREATE,
    PROJECT_ISSUES_AND_UPGRADES_UPDATE,
    PROJECT_ISSUES_AND_UPGRADES_SUBMIT,
    PROJECT_ISSUES_AND_UPGRADES_RESOLVE,
    PROJECT_ISSUES_AND_UPGRADES_DELETE,
    PROJECT_TASKS_CREATE_ABILITY,
    PROJECT_SPRINTS_VIEW_ABILITY,
} from '@/ability';
import { useProjectDetail } from '@hooks/projects/detail/useProjectDetail';
import { contentToApiMentions, resolveDescriptionMentionsForDisplay } from '@utils/richTextMentions';
import { buildTaskDescription } from '@hooks/projects/projectHelpers';
import { validateLinkUrlInput } from '@utils/linkUrlValidation.jsx';
import {
    createProjectIssueOrUpgrade,
    createProjectIssueOrUpgradeTask,
    deleteProjectIssueOrUpgrade,
    getProjectIssuesAndUpgrades,
    getProjectPhases,
    resolveProjectIssueOrUpgrade,
    submitProjectIssueOrUpgrade,
    updateProjectIssueOrUpgrade,
} from '@services/projects.service';
import { invalidateProjectQueries, invalidateProjectQueriesExceptIssueUpgradeList } from '@hooks/projects/projectQueryInvalidation';
import { useProjectMilestones } from '@hooks/projects/useProjectMilestones';
import { useProjectSprints } from '@hooks/projects/useProjectSprints';
import {
    truncateFileName,
    getMilestoneDisplayLabel,
    getSprintDisplayLabel,
} from '@hooks/projects/projectHelpers';

export const PROJECT_ISSUES_AND_UPGRADES_QUERY_KEY = (projectId, params) => [
    'projects',
    projectId,
    'issues-and-upgrades',
    params,
];

export const ISSUE_UPGRADE_COL = {
    ACTIVE: 'active',
    SUBMITTED: 'submitted',
    RESOLVED: 'resolved',
};

const MENTION_USERS_LIMIT = 100;

function getIssueOrUpgradeId(item) {
    return item?.issueOrUpgradeId ?? item?.issue_or_upgrade_id ?? item?.id ?? null;
}

function normalizeIssueColumn(item) {
    const state = String(item?.state ?? item?.workflowState ?? '').trim().toLowerCase();
    if (state) {
        if (['resolved', 'resolve', 'closed', 'done', 'completed'].includes(state)) return ISSUE_UPGRADE_COL.RESOLVED;
        if (
            ['submitted', 'submit', 'submitted_for_review', 'in_review', 'review', 'genuine', 'improper'].includes(state)
        ) {
            return ISSUE_UPGRADE_COL.SUBMITTED;
        }
    }
    const s = String(item?.status ?? '').trim().toLowerCase();
    if (['resolved', 'resolve', 'closed', 'done', 'completed'].includes(s)) return ISSUE_UPGRADE_COL.RESOLVED;
    if (['submitted', 'submit', 'submitted_for_review', 'in_review', 'review'].includes(s)) {
        return ISSUE_UPGRADE_COL.SUBMITTED;
    }
    return ISSUE_UPGRADE_COL.ACTIVE;
}

function dedupeMentionEntities(items, getId) {
    const map = new Map();
    for (const it of Array.isArray(items) ? items : []) {
        if (!it) continue;
        const id = String(getId(it) ?? '').trim();
        if (!id) continue;
        if (!map.has(id)) map.set(id, it);
    }
    return [...map.values()];
}

/** Mention catalog for editors: only from list rows (no project-by-id). */
function collectMentionUsersFromIssueUpgradeRows(rows, limit = MENTION_USERS_LIMIT) {
    const collected = [];
    for (const row of Array.isArray(rows) ? rows : []) {
        const dm = row?.descriptionMentions;
        const users = Array.isArray(dm?.users) ? dm.users : [];
        collected.push(...users);
        const cb = row?.createdBy;
        if (cb && typeof cb === 'object' && cb.id) collected.push(cb);
        const sb = row?.submittedBy;
        if (sb && typeof sb === 'object' && sb.id) collected.push(sb);
    }
    return dedupeMentionEntities(collected, (u) => u?.id).slice(0, limit);
}

function partitionIssueRows(list) {
    const active = [];
    const submitted = [];
    const resolved = [];
    for (const item of Array.isArray(list) ? list : []) {
        const col = normalizeIssueColumn(item);
        if (col === ISSUE_UPGRADE_COL.RESOLVED) resolved.push(item);
        else if (col === ISSUE_UPGRADE_COL.SUBMITTED) submitted.push(item);
        else active.push(item);
    }
    return { active, submitted, resolved };
}

function normalizeLinkAttachments(links) {
    const list = Array.isArray(links) ? links : [];
    return list
        .map(({ url, title }) => ({
            type: 'link',
            url,
            title: title || url,
        }))
        .filter((x) => x.url);
}

function iauTaskNormalizeBinaryFiles(files) {
    if (!Array.isArray(files)) return [];
    return files
        .map((f) => f?.originFileObj ?? f?.file ?? f)
        .filter((f) => f instanceof File || f instanceof Blob);
}

/** Same shape as sprint task create links: `{ type: 'link', url, title? }[]`. */
function iauTaskNormalizeLinkAttachments(attachments) {
    if (!Array.isArray(attachments)) return [];
    return attachments
        .filter((att) => att && att.url)
        .map((att) => {
            const type = String(att.type || 'link').toLowerCase();
            const normalizedType = type === 'file' || type === 'image' || type === 'link' ? type : 'link';
            return {
                type: normalizedType,
                url: att.url,
                ...(att.title ? { title: att.title } : {}),
            };
        });
}

function normalizeExistingAttachments(existingAttachments) {
    const list = Array.isArray(existingAttachments) ? existingAttachments : [];
    return list
        .map((x) => ({
            type: x?.type || 'file',
            url: x?.url,
            title: x?.title || x?.url,
        }))
        .filter((x) => x.url);
}

function buildCreateIssuePayload(values, files, links) {
    const descriptionHtml = contentToApiMentions(values?.description ?? '');
    const description = buildTaskDescription(descriptionHtml);
    if (!description?.markdown) return null;
    const title = String(values?.title ?? '').trim();
    if (!title) return null;
    const linkAttachments = normalizeLinkAttachments(links);
    const payload = {
        title,
        type: String(values?.type || 'issue').toLowerCase() === 'upgrade' ? 'upgrade' : 'issue',
        description: {
            markdown: description.markdown,
        },
    };
    if (Array.isArray(files) && files.length > 0) {
        const formData = new FormData();
        formData.append('title', payload.title);
        formData.append('type', payload.type);
        formData.append('description', JSON.stringify(payload.description));
        if (linkAttachments.length > 0) {
            formData.append(
                'attachmentUrls',
                JSON.stringify(
                    linkAttachments,
                ),
            );
        }
        files.forEach((file) => formData.append('files', file));
        return formData;
    }
    if (linkAttachments.length > 0) {
        payload.attachments = linkAttachments;
    }
    return payload;
}

function buildEditIssuePayload(values, { files, links, existingAttachments }) {
    // Always use multipart on edit so backend keeps existing files consistently.
    const descriptionHtml = contentToApiMentions(values?.description ?? '');
    const description = buildTaskDescription(descriptionHtml);
    if (!description?.markdown) return null;
    const title = String(values?.title ?? '').trim();
    if (!title) return null;

    const keep = normalizeExistingAttachments(existingAttachments);
    const linkAttachments = normalizeLinkAttachments(links);
    const mergedAttachments = [...keep, ...linkAttachments];

    const formData = new FormData();
    formData.append('title', title);
    formData.append('type', String(values?.type || 'issue').toLowerCase() === 'upgrade' ? 'upgrade' : 'issue');
    formData.append('description', JSON.stringify({ markdown: description.markdown }));
    formData.append('attachmentUrls', JSON.stringify(mergedAttachments));
    (Array.isArray(files) ? files : []).forEach((file) => {
        if (file) formData.append('files', file);
    });
    return formData;
}

function pickFirstNonEmpty(...vals) {
    for (const v of vals) {
        if (v != null && v !== '') return v;
    }
    return undefined;
}

/** Build a linked-task row for the issues list cache from POST …/tasks response + form body. */
function buildLinkedTaskStubFromCreateResponse(created, body, sprintList) {
    const sprintId = body?.sprintId;
    const sp = Array.isArray(sprintList) ? sprintList.find((s) => String(s?.id ?? '') === String(sprintId ?? '')) : null;
    const sprintNameFallback = sp?.name ?? sp?.title ?? '';
    const c = created != null && typeof created === 'object' ? created : {};
    const taskId = pickFirstNonEmpty(c.taskId, c.TaskId, c.id);
    return {
        ...c,
        taskId,
        sprintTaskId: pickFirstNonEmpty(c.sprintTaskId, c.sprint_task_id, c.id),
        sprintId: pickFirstNonEmpty(c.sprintId, sprintId),
        sprintName: pickFirstNonEmpty(c.sprintName, c.sprint_name, sprintNameFallback),
        title: pickFirstNonEmpty(c.title, body?.title, 'Task'),
        name: pickFirstNonEmpty(c.name, c.title, body?.title),
        sprintTaskStatus: pickFirstNonEmpty(c.sprintTaskStatus, c.sprint_task_status, c.status, 'todo'),
        status: pickFirstNonEmpty(c.status, c.sprintTaskStatus, 'todo'),
        taskNumber: pickFirstNonEmpty(c.taskNumber, c.task_number, c.number),
        sortOrder: c.sortOrder ?? c.sort_order,
    };
}

/**
 * Issues list GET often omits `tasks[]`; merge the new link so kanban cards can show counts without waiting on backend list shape.
 */
function mergeCreatedTaskIntoIssueUpgradeListCaches(queryClient, projectId, issueOrUpgradeId, created, body, sprintList) {
    const idStr = String(issueOrUpgradeId ?? '').trim();
    if (!projectId || !idStr || !queryClient) return;
    const stub = buildLinkedTaskStubFromCreateResponse(created, body, sprintList);
    const tid = String(stub.taskId ?? stub.sprintTaskId ?? '').trim();
    if (!tid) return;

    queryClient.setQueriesData({ queryKey: ['projects', projectId, 'issues-and-upgrades'] }, (old) => {
        if (!old || !Array.isArray(old.data)) return old;
        let changed = false;
        const nextData = old.data.map((row) => {
            const rid = String(getIssueOrUpgradeId(row) ?? '').trim();
            if (rid !== idStr) return row;
            changed = true;
            const prev = Array.isArray(row.tasks) ? row.tasks : [];
            const base = tid
                ? prev.filter((t) => String(t?.taskId ?? t?.id ?? t?.sprintTaskId ?? '').trim() !== tid)
                : [...prev];
            return { ...row, tasks: [...base, stub] };
        });
        return changed ? { ...old, data: nextData } : old;
    });
}
function useIssueOrUpgradeCreateTaskModal(projectId, mentionUsers, project) {
    const can = useCanCheck();
    const queryClient = useQueryClient();
    const canCreateTask = can(PROJECT_TASKS_CREATE_ABILITY);
    const canViewSprints = can(PROJECT_SPRINTS_VIEW_ABILITY);

    const [modalOpen, setModalOpen] = useState(false);
    const [sourceIssueRow, setSourceIssueRow] = useState(null);
    const [taskForm] = Form.useForm();
    const [linkModalForm] = Form.useForm();
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const linkModalCallbackRef = useRef(null);
    const [taskPendingAttachments, setTaskPendingAttachments] = useState([]);
    const [pendingLinkAttachments, setPendingLinkAttachments] = useState([]);
    const defaultsAppliedRef = useRef(false);

    const usePhasesEnabled = project ? project.capabilities?.usePhases !== false : false;
    const useMilestonesEnabled = project ? project.capabilities?.useMilestones !== false : false;
    const useSprintsEnabled = project ? project.capabilities?.useSprints !== false : false;

    const listEnabled = !!projectId && modalOpen && canCreateTask;
    const { milestones = [] } = useProjectMilestones(projectId, { enabled: listEnabled && useMilestonesEnabled });
    const { sprints = [] } = useProjectSprints(projectId, { enabled: listEnabled && useSprintsEnabled });

    const { data: phasesData = [] } = useQuery({
        queryKey: ['projects', projectId, 'phases'],
        queryFn: () => getProjectPhases(projectId),
        enabled: Boolean(projectId && listEnabled && usePhasesEnabled && !useMilestonesEnabled),
        staleTime: 0,
    });

    const sprintList = useMemo(() => (Array.isArray(sprints) ? sprints : []), [sprints]);
    const milestoneList = useMemo(() => (Array.isArray(milestones) ? milestones : []), [milestones]);
    const phaseList = useMemo(() => (Array.isArray(phasesData) ? phasesData : []), [phasesData]);

    const mentionMilestones = milestoneList;
    const mentionSprints = sprintList;

    const createTaskMutation = useMutation({
        mutationFn: ({ issueOrUpgradeId, body }) => {
            const files = iauTaskNormalizeBinaryFiles(body.files);
            const linkAttachments = iauTaskNormalizeLinkAttachments(body.attachments);
            const description = body.description && typeof body.description === 'object' ? body.description : null;
            const base = {
                title: typeof body.title === 'string' ? body.title : '',
                type: body.type || 'feature',
                priority: body.priority || 'medium',
            };
            if (body.sprintId) base.sprintId = body.sprintId;
            if (body.milestoneId) base.milestoneId = body.milestoneId;
            if (body.phaseId) base.phaseId = body.phaseId;

            const forceMultipart = Array.isArray(body.attachments) && body.attachments.some((att) => {
                if (!att || !att.url) return false;
                const type = String(att.type || 'link').toLowerCase();
                return type !== 'link';
            });

            if (files?.length || forceMultipart) {
                const formData = new FormData();
                if (base.sprintId) formData.append('sprintId', base.sprintId);
                if (base.milestoneId) formData.append('milestoneId', base.milestoneId);
                if (base.phaseId) formData.append('phaseId', base.phaseId);
                formData.append('title', base.title);
                formData.append('type', base.type);
                formData.append('priority', base.priority);
                if (description) formData.append('description', JSON.stringify(description));
                if (linkAttachments.length > 0) formData.append('attachmentUrls', JSON.stringify(linkAttachments));
                files.forEach((file) => formData.append('files', file));
                return createProjectIssueOrUpgradeTask(projectId, issueOrUpgradeId, formData);
            }
            const payload = {
                ...base,
                ...(description ? { description } : {}),
                ...(linkAttachments.length > 0 ? { attachments: linkAttachments } : {}),
            };
            return createProjectIssueOrUpgradeTask(projectId, issueOrUpgradeId, payload);
        },
        onSuccess: (created, variables) => {
            mergeCreatedTaskIntoIssueUpgradeListCaches(
                queryClient,
                projectId,
                variables.issueOrUpgradeId,
                created,
                variables.body,
                sprintList,
            );
            invalidateProjectQueriesExceptIssueUpgradeList(queryClient, projectId);
        },
    });

    const closeModal = useCallback(() => {
        if (createTaskMutation.isPending) return;
        setModalOpen(false);
        setSourceIssueRow(null);
        defaultsAppliedRef.current = false;
        taskForm.resetFields();
        linkModalForm.resetFields();
        setLinkModalOpen(false);
        linkModalCallbackRef.current = null;
        setPendingLinkAttachments([]);
        setTaskPendingAttachments((prev) => {
            prev.forEach((p) => {
                if (p?.preview) {
                    try {
                        URL.revokeObjectURL(p.preview);
                    } catch {
                        // ignore
                    }
                }
            });
            return [];
        });
    }, [createTaskMutation.isPending, taskForm, linkModalForm]);

    const openModal = useCallback(
        (issueRow) => {
            if (!canCreateTask || !issueRow) return;
            defaultsAppliedRef.current = false;
            setSourceIssueRow(issueRow);
            
            const initialLinks = Array.isArray(issueRow?.attachments)
                ? issueRow.attachments.map((att, index) => ({
                      id: att.id || `iau-task-link-copied-${Date.now()}-${index}`,
                      url: att.url,
                      title: att.title || att.name || att.url,
                      type: att.type || 'link',
                  }))
                : [];
            setPendingLinkAttachments(initialLinks);
            setTaskPendingAttachments([]);
            linkModalForm.resetFields();
            taskForm.resetFields();
            const baseTitle = String(issueRow?.title || '').trim();
            const descriptionMarkdown = getIssueOrUpgradeMarkdown(issueRow);
            taskForm.setFieldsValue({
                priority: 'medium',
                type: 'feature',
                description: descriptionMarkdown || '<p>Task created from issue/upgrade workflow.</p>',
                title: baseTitle ? `Fix issue raised in IAU · ${baseTitle}` : 'Fix issue raised in IAU',
            });
            setModalOpen(true);
        },
        [canCreateTask, taskForm, linkModalForm],
    );

    useEffect(() => {
        if (!modalOpen || defaultsAppliedRef.current) return;
        if (useMilestonesEnabled) {
            if (!milestoneList.length && !sprintList.length) return;
            const mids = milestoneList.map((m) => String(m?.id ?? '')).filter(Boolean);
            const firstMilestoneId = mids.length ? mids[0] : null;
            const scoped =
                firstMilestoneId != null
                    ? sprintList.filter((sp) => String(sp?.milestoneId ?? sp?.milestone?.id ?? '') === String(firstMilestoneId))
                    : sprintList;
            const firstSprint = scoped[0] || sprintList[0];
            taskForm.setFieldsValue({
                milestoneId: firstMilestoneId || undefined,
                sprintId: useSprintsEnabled ? (firstSprint?.id || undefined) : undefined,
            });
            defaultsAppliedRef.current = true;
        } else if (usePhasesEnabled) {
            if (!phaseList.length && !sprintList.length) return;
            const pids = phaseList.map((p) => String(p?.id ?? '')).filter(Boolean);
            const firstPhaseId = pids.length ? pids[0] : null;
            const scoped =
                firstPhaseId != null
                    ? sprintList.filter((sp) => String(sp?.phaseId ?? sp?.phase?.id ?? '') === String(firstPhaseId))
                    : sprintList;
            const firstSprint = scoped[0] || sprintList[0];
            taskForm.setFieldsValue({
                phaseId: firstPhaseId || undefined,
                sprintId: useSprintsEnabled ? (firstSprint?.id || undefined) : undefined,
            });
            defaultsAppliedRef.current = true;
        } else if (useSprintsEnabled) {
            if (!sprintList.length) return;
            taskForm.setFieldsValue({
                sprintId: sprintList[0]?.id || undefined,
            });
            defaultsAppliedRef.current = true;
        } else {
            defaultsAppliedRef.current = true;
        }
    }, [modalOpen, milestoneList, phaseList, sprintList, taskForm, project, useMilestonesEnabled, usePhasesEnabled, useSprintsEnabled]);

    const handleTaskFilesSelected = useCallback((files) => {
        if (!files?.length) return;
        const next = files.map((file, i) => ({
            id: `iau-task-file-${Date.now()}-${i}`,
            file,
            name: file.name,
            type: file.type,
            preview: (file.type || '').startsWith('image/') ? URL.createObjectURL(file) : null,
        }));
        setTaskPendingAttachments((prev) => [...prev, ...next]);
    }, []);

    const removeTaskAttachment = useCallback(
        (id) => {
            setTaskPendingAttachments((prev) => {
                const idx = prev.findIndex((p) => p.id === id);
                if (idx < 0) return prev.filter((p) => p.id !== id);
                const target = prev[idx];
                if (target?.preview) {
                    try {
                        URL.revokeObjectURL(target.preview);
                    } catch {
                        // ignore
                    }
                }
                let content = taskForm.getFieldValue('description') || '';
                const linkRegex = new RegExp(`<a\\s(?=[^>]*href="#attachment-${idx}")[^>]*>[\\s\\S]*?</a>`, 'gi');
                content = content.replace(linkRegex, '');
                for (let j = prev.length - 1; j > idx; j--) {
                    content = content.replace(new RegExp(`#attachment-${j}`, 'g'), `#attachment-${j - 1}`);
                }
                taskForm.setFieldValue('description', content);
                return prev.filter((p) => p.id !== id);
            });
        },
        [taskForm],
    );

    const removePendingLinkAttachment = useCallback(
        (idOrHref) => {
            const key = String(idOrHref ?? '').trim();
            if (!key) return;
            setPendingLinkAttachments((prev) => {
                const match =
                    prev.find((x) => String(x.id) === key) ?? prev.find((x) => String(x.url) === key);
                const next = prev.filter((x) => String(x.id) !== key && String(x.url) !== key);
                const href = match?.url || (key.startsWith('http') ? key : null);
                if (href) {
                    const esc = href.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    const content = String(taskForm.getFieldValue('description') || '');
                    const re = new RegExp(`<a\\s(?=[^>]*href=["']${esc}["'])[^>]*>[\\s\\S]*?<\\/a>`, 'i');
                    taskForm.setFieldValue('description', content.replace(re, ''));
                }
                return next;
            });
        },
        [taskForm],
    );

    const handleEditorLinkRemoved = useCallback(
        (href) => {
            if (!href) return;
            removePendingLinkAttachment(String(href));
        },
        [removePendingLinkAttachment],
    );

    const watchedTaskDesc = Form.useWatch('description', taskForm);
    useEffect(() => {
        const html = watchedTaskDesc;
        if (!html || typeof html !== 'string') return;
        setPendingLinkAttachments((prev) => prev.filter((x) => !x?.url || html.includes(String(x.url))));
    }, [watchedTaskDesc]);

    const handleLinkRequest = useCallback(
        (insertLink) => {
            linkModalCallbackRef.current = insertLink;
            linkModalForm.resetFields();
            setLinkModalOpen(true);
        },
        [linkModalForm],
    );

    const handleLinkModalSubmit = useCallback(() => {
        linkModalForm.validateFields().then((values) => {
            const rawUrl = (values.url || '').trim();
            const title = (values.title || '').trim();
            const check = validateLinkUrlInput(rawUrl);
            if (!check.ok) {
                linkModalForm.setFields([
                    { name: 'url', errors: [check.message || 'Enter a valid URL (https://example.com)'] },
                ]);
                return;
            }
            const normalizedUrl = check.normalized;
            const displayTitle = title || normalizedUrl;
            const insertLink = linkModalCallbackRef.current;
            if (insertLink) insertLink({ url: normalizedUrl, title: displayTitle });
            setPendingLinkAttachments((prev) => [
                ...prev,
                { id: `iau-task-link-${Date.now()}`, url: normalizedUrl, title: displayTitle },
            ]);
            setLinkModalOpen(false);
            linkModalCallbackRef.current = null;
        });
    }, [linkModalForm]);

    const handleSubmit = useCallback(() => {
        const issueOrUpgradeId = getIssueOrUpgradeId(sourceIssueRow);
        if (!issueOrUpgradeId) {
            message.error('Missing issue or upgrade id.');
            return;
        }
        taskForm.validateFields().then((values) => {
            const sprintId = values.sprintId ?? values?.sprint?.id;
            const useSprintsEnabled = project ? project.capabilities?.useSprints !== false : false;
            if (useSprintsEnabled && !sprintId) {
                message.warning('Select a sprint for this task.');
                return;
            }
            const descriptionHtml = contentToApiMentions(values.description ?? '');
            const description = buildTaskDescription(descriptionHtml);
            createTaskMutation.mutate(
                {
                    issueOrUpgradeId: String(issueOrUpgradeId),
                    body: {
                        ...(sprintId ? { sprintId: String(sprintId) } : {}),
                        ...(values.milestoneId ? { milestoneId: String(values.milestoneId) } : {}),
                        ...(values.phaseId ? { phaseId: String(values.phaseId) } : {}),
                        title: (values.title ?? '').trim() || 'Task',
                        ...(description ? { description } : {}),
                        type: values.type || 'feature',
                        priority: values.priority || 'medium',
                        ...(taskPendingAttachments.length > 0 && {
                            files: taskPendingAttachments.map((p) => p.file).filter(Boolean),
                        }),
                        ...(pendingLinkAttachments.length > 0 && {
                            attachments: pendingLinkAttachments.map(({ url, title: t }) => ({ url, title: t || url })),
                        }),
                    },
                },
                {
                    onSuccess: () => {
                        message.success('Task created');
                        closeModal();
                    },
                    onError: (err) =>
                        message.error(err?.response?.data?.message || err?.message || 'Failed to create task'),
                },
            );
        });
    }, [sourceIssueRow, taskForm, taskPendingAttachments, pendingLinkAttachments, createTaskMutation, closeModal, project]);

    return {
        openCreateTaskFromIssue: openModal,
        canCreateTaskFromIssue: canCreateTask,
        canViewSprintsForTaskCreate: canViewSprints,
        createTaskFromIssueModalOpen: modalOpen,
        closeCreateTaskFromIssueModal: closeModal,
        handleCreateTaskFromIssueSubmit: handleSubmit,
        createTaskFromIssuePending: createTaskMutation.isPending,
        taskForm,
        taskPendingAttachments,
        handleTaskFilesSelected,
        removeTaskAttachment,
        pendingLinkAttachments,
        removePendingLinkAttachment,
        handleEditorLinkRemoved,
        handleLinkRequest,
        linkModalOpen,
        setLinkModalOpen,
        linkModalForm,
        linkModalCallbackRef,
        handleLinkModalSubmit,
        mentionUsers,
        mentionMilestones,
        mentionSprints,
        milestoneList,
        sprintList,
        truncateFileName,
        getMilestoneDisplayLabel,
        getSprintDisplayLabel,
        project,
        phaseList,
        useMilestonesEnabled,
        usePhasesEnabled,
        useSprintsEnabled,
    };
}

export function useProjectIssuesAndUpgrades(projectId, params = { page: 1 }) {
    const can = useCanCheck();
    const queryClient = useQueryClient();
    const canView = can(PROJECT_ISSUES_AND_UPGRADES_VIEW);
    const canCreate = can(PROJECT_ISSUES_AND_UPGRADES_CREATE);
    const canUpdate = can(PROJECT_ISSUES_AND_UPGRADES_UPDATE);
    const canSubmit = can(PROJECT_ISSUES_AND_UPGRADES_SUBMIT);
    const canResolve = can(PROJECT_ISSUES_AND_UPGRADES_RESOLVE);
    const canDelete = can(PROJECT_ISSUES_AND_UPGRADES_DELETE);
    const canDrag = canUpdate || canSubmit || canResolve;
    const pageParam = params?.page ?? 1;

    const { data: project } = useProjectDetail(projectId, { enabled: !!projectId });
    const useTasksEnabled = project?.capabilities?.useTasks !== false;

    const query = useQuery({
        queryKey: ['projects', projectId, 'issues-and-upgrades', { page: pageParam }],
        queryFn: async () => {
            if (pageParam === 1) {
                const res = await getProjectIssuesAndUpgrades(projectId, { page: 1, limit: 100 });
                return { data: res?.data ?? [], total: res?.total ?? 0 };
            }
            
            const prevData = queryClient.getQueryData(['projects', projectId, 'issues-and-upgrades', { page: pageParam - 1 }]);
            const prevList = Array.isArray(prevData?.data) ? prevData.data : [];
            
            if (prevList.length > 0) {
                const res = await getProjectIssuesAndUpgrades(projectId, { page: pageParam, limit: 100 });
                return {
                    data: [...prevList, ...(res?.data ?? [])],
                    total: res?.total ?? 0,
                };
            } else {
                const promises = [];
                for (let p = 1; p <= pageParam; p++) {
                    promises.push(getProjectIssuesAndUpgrades(projectId, { page: p, limit: 100 }));
                }
                const results = await Promise.all(promises);
                const data = [];
                let total = 0;
                results.forEach((res) => {
                    data.push(...(res?.data ?? []));
                    if (res?.total != null) total = res.total;
                });
                return { data, total };
            }
        },
        enabled: !!projectId && canView && useTasksEnabled,
        placeholderData: keepPreviousData,
    });

    const rows = useMemo(
        () => (Array.isArray(query.data?.data) ? query.data.data : []),
        [query.data?.data],
    );

    const mentionUsers = useMemo(() => collectMentionUsersFromIssueUpgradeRows(rows), [rows]);

    const createTaskFromIssue = useIssueOrUpgradeCreateTaskModal(projectId, mentionUsers, project);

    const partitioned = useMemo(() => partitionIssueRows(rows), [rows]);

    const createMutation = useMutation({
        mutationFn: (body) => createProjectIssueOrUpgrade(projectId, body),
        onSuccess: () => invalidateProjectQueries(queryClient, projectId),
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, body }) => updateProjectIssueOrUpgrade(projectId, id, body),
        onSuccess: () => invalidateProjectQueries(queryClient, projectId),
    });
    const submitMutation = useMutation({
        mutationFn: ({ id, body }) => submitProjectIssueOrUpgrade(projectId, id, body),
        onSuccess: () => invalidateProjectQueries(queryClient, projectId),
    });
    const resolveMutation = useMutation({
        mutationFn: (id) => resolveProjectIssueOrUpgrade(projectId, id),
        onSuccess: () => invalidateProjectQueries(queryClient, projectId),
    });
    const deleteMutation = useMutation({
        mutationFn: (id) => deleteProjectIssueOrUpgrade(projectId, id),
        onSuccess: () => invalidateProjectQueries(queryClient, projectId),
    });

    const createModal = useProjectIssueUpgradeCreateModal({
        canCreate,
        canUpdate,
        mentionUsers,
        createIssueOrUpgrade: (payload, callbacks = {}) => {
            createMutation.mutate(payload, {
                onSuccess: callbacks.onSuccess,
                onError: callbacks.onError,
            });
        },
        updateIssueOrUpgrade: (id, payload, callbacks = {}) => {
            updateMutation.mutate(
                { id, body: payload },
                {
                    onSuccess: callbacks.onSuccess,
                    onError: callbacks.onError,
                },
            );
        },
        createPending: createMutation.isPending || updateMutation.isPending,
    });

    const drag = useProjectIssueUpgradeKanbanDrag({
        canSubmit,
        canResolve,
        canUpdate,
        onMove: ({ id, targetColumn, callbacks }) => {
            if (targetColumn === ISSUE_UPGRADE_COL.SUBMITTED) {
                submitMutation.mutate(
                    { id, body: { state: 'genuine' } },
                    {
                    onSuccess: callbacks?.onSuccess,
                    onError: callbacks?.onError,
                    },
                );
                return;
            }
            if (targetColumn === ISSUE_UPGRADE_COL.RESOLVED) {
                resolveMutation.mutate(id, {
                    onSuccess: callbacks?.onSuccess,
                    onError: callbacks?.onError,
                });
                return;
            }
            updateMutation.mutate(
                { id, body: { status: 'active' } },
                {
                    onSuccess: callbacks?.onSuccess,
                    onError: callbacks?.onError,
                },
            );
        },
    });

    const submitIssueOrUpgrade = useCallback(
        (id, submitBody = {}, callbacks = {}) => {
            if (!id || !canSubmit) return;
            let payload = submitBody;
            if (submitBody?.files?.length > 0) {
                payload = new FormData();
                payload.append('state', submitBody.state || 'genuine');
                if (submitBody.reason) {
                    payload.append('reason', submitBody.reason);
                }
                if (submitBody.attachments?.length > 0) {
                    payload.append('attachmentUrls', JSON.stringify(submitBody.attachments));
                }
                submitBody.files.forEach((f) => payload.append('files', f));
            } else {
                payload = {
                    state: submitBody.state || 'genuine',
                    ...(submitBody.reason ? { reason: submitBody.reason } : {}),
                    ...(submitBody.attachments?.length > 0 ? { attachments: submitBody.attachments } : {}),
                };
            }
            submitMutation.mutate(
                { id, body: payload },
                {
                    onSuccess: callbacks.onSuccess,
                    onError: callbacks.onError,
                },
            );
        },
        [canSubmit, submitMutation],
    );

    const resolveIssueOrUpgrade = useCallback(
        (id, callbacks = {}) => {
            if (!id) return;
            resolveMutation.mutate(id, {
                onSuccess: callbacks.onSuccess,
                onError: callbacks.onError,
            });
        },
        [resolveMutation],
    );

    const promptDeleteIssueOrUpgrade = useCallback(
        (row, callbacks = {}) => {
            const id = getIssueOrUpgradeId(row);
            if (!id || !canDelete) return;
            Modal.confirm({
                title: 'Delete this issue or upgrade?',
                content: 'This cannot be undone.',
                okText: 'Yes, delete',
                cancelText: 'No',
                okType: 'danger',
                onOk: () =>
                    new Promise((resolve, reject) => {
                        deleteMutation.mutate(id, {
                            onSuccess: () => {
                                message.success('Deleted');
                                callbacks.onSuccess?.();
                                resolve();
                            },
                            onError: (err) => {
                                message.error(
                                    err?.response?.data?.message || err?.message || 'Failed to delete',
                                );
                                reject(err);
                            },
                        });
                    }),
            });
        },
        [canDelete, deleteMutation],
    );

    return {
        activeRows: partitioned.active,
        submittedRows: partitioned.submitted,
        resolvedRows: partitioned.resolved,
        total: query.data?.total ?? rows.length,
        isLoading: query.isPending,
        isFetching: query.isFetching,
        canView,
        canCreate,
        canUpdate,
        canSubmit,
        canResolve,
        canDelete,
        canDrag,
        createPending: createMutation.isPending,
        createModal,
        submitPending: submitMutation.isPending,
        resolvePending: resolveMutation.isPending,
        submitIssueOrUpgrade,
        resolveIssueOrUpgrade,
        deletePending: deleteMutation.isPending,
        promptDeleteIssueOrUpgrade,
        ...drag,
        refetch: query.refetch,
        mentionUsers: createTaskFromIssue.mentionUsers,
        mentionMilestones: createTaskFromIssue.mentionMilestones,
        mentionSprints: createTaskFromIssue.mentionSprints,
        openCreateTaskFromIssue: createTaskFromIssue.openCreateTaskFromIssue,
        canCreateTaskFromIssue: createTaskFromIssue.canCreateTaskFromIssue,
        canViewSprintsForTaskCreate: createTaskFromIssue.canViewSprintsForTaskCreate,
        createTaskFromIssuePending: createTaskFromIssue.createTaskFromIssuePending,
        createTaskFromIssueModalProps: {
            modalOpen: createTaskFromIssue.createTaskFromIssueModalOpen,
            onModalCancel: createTaskFromIssue.closeCreateTaskFromIssueModal,
            onModalSubmit: createTaskFromIssue.handleCreateTaskFromIssueSubmit,
            submitPending: createTaskFromIssue.createTaskFromIssuePending,
            taskForm: createTaskFromIssue.taskForm,
            taskPendingAttachments: createTaskFromIssue.taskPendingAttachments,
            handleTaskFilesSelected: createTaskFromIssue.handleTaskFilesSelected,
            removeTaskAttachment: createTaskFromIssue.removeTaskAttachment,
            pendingLinkAttachments: createTaskFromIssue.pendingLinkAttachments,
            removePendingLinkAttachment: createTaskFromIssue.removePendingLinkAttachment,
            handleEditorLinkRemoved: createTaskFromIssue.handleEditorLinkRemoved,
            handleLinkRequest: createTaskFromIssue.handleLinkRequest,
            linkModalOpen: createTaskFromIssue.linkModalOpen,
            setLinkModalOpen: createTaskFromIssue.setLinkModalOpen,
            linkModalForm: createTaskFromIssue.linkModalForm,
            linkModalCallbackRef: createTaskFromIssue.linkModalCallbackRef,
            handleLinkModalSubmit: createTaskFromIssue.handleLinkModalSubmit,
            mentionUsers: createTaskFromIssue.mentionUsers,
            mentionMilestones: createTaskFromIssue.mentionMilestones,
            mentionSprints: createTaskFromIssue.mentionSprints,
            milestoneList: createTaskFromIssue.milestoneList,
            sprintList: createTaskFromIssue.sprintList,
            truncateFileName: createTaskFromIssue.truncateFileName,
            getMilestoneDisplayLabel: createTaskFromIssue.getMilestoneDisplayLabel,
            getSprintDisplayLabel: createTaskFromIssue.getSprintDisplayLabel,
            canCreateTaskFromIssue: createTaskFromIssue.canCreateTaskFromIssue,
            canViewSprintsForTaskCreate: createTaskFromIssue.canViewSprintsForTaskCreate,
            useMilestonesEnabled: createTaskFromIssue.useMilestonesEnabled,
            usePhasesEnabled: createTaskFromIssue.usePhasesEnabled,
            useSprintsEnabled: createTaskFromIssue.useSprintsEnabled,
            phaseList: createTaskFromIssue.phaseList,
        },
    };
}

function useProjectIssueUpgradeCreateModal({
    canCreate,
    canUpdate,
    mentionUsers,
    createIssueOrUpgrade,
    updateIssueOrUpgrade,
    createPending,
}) {
    const [form] = Form.useForm();
    const [modalOpen, setModalOpen] = useState(false);
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const [pendingLinks, setPendingLinks] = useState([]);
    const [existingAttachments, setExistingAttachments] = useState([]);
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [linkModalForm] = Form.useForm();
    const linkModalCallbackRef = useRef(null);
    const [editingItem, setEditingItem] = useState(null);

    const openModal = useCallback(() => {
        if (!canCreate) return;
        setEditingItem(null);
        form.resetFields();
        setModalOpen(true);
    }, [canCreate, form]);

    const openEditModal = useCallback(
        (item) => {
            if (!canUpdate || !item) return;
            setEditingItem(item);
            form.setFieldsValue({
                title: String(item?.title || '').trim(),
                type: getIssueOrUpgradeType(item),
                description: getIssueOrUpgradeMarkdown(item),
            });
            const linksFromPayload = Array.isArray(item?.description?.links)
                ? item.description.links
                : Array.isArray(item?.attachments)
                    ? item.attachments.filter((x) => String(x?.type || '').toLowerCase() === 'link')
                    : [];
            setPendingLinks(
                linksFromPayload.map((x, i) => ({
                    id: `issue-upgrade-edit-link-${Date.now()}-${i}`,
                    url: x?.url || '',
                    title: x?.title || x?.url || '',
                })),
            );
            const filesFromPayload = Array.isArray(item?.attachments)
                ? item.attachments.filter((x) => String(x?.type || '').toLowerCase() !== 'link')
                : [];
            setExistingAttachments(
                filesFromPayload
                    .map((x, i) => ({
                        id: x?.id ?? `issue-upgrade-existing-att-${Date.now()}-${i}`,
                        type: x?.type || 'file',
                        url: x?.url || '',
                        title: x?.title || x?.url || 'Attachment',
                    }))
                    .filter((x) => Boolean(x.url)),
            );
            setPendingAttachments([]);
            setModalOpen(true);
        },
        [canUpdate, form],
    );

    const closeModal = useCallback(() => {
        if (createPending) return;
        setModalOpen(false);
        setPendingAttachments((prev) => {
            prev.forEach((p) => {
                if (p?.preview) {
                    try {
                        URL.revokeObjectURL(p.preview);
                    } catch {
                        // ignore
                    }
                }
            });
            return [];
        });
        setPendingLinks([]);
        setExistingAttachments([]);
        setEditingItem(null);
        setLinkModalOpen(false);
        linkModalCallbackRef.current = null;
        form.resetFields();
        linkModalForm.resetFields();
    }, [createPending, form, linkModalForm]);

    const handleFilesSelected = useCallback((files) => {
        if (!files?.length) return;
        const next = files.map((file, i) => ({
            id: `issue-upgrade-file-${Date.now()}-${i}`,
            file,
            name: file.name,
            type: file.type,
            preview: (file.type || '').startsWith('image/') ? URL.createObjectURL(file) : null,
        }));
        setPendingAttachments((prev) => [...prev, ...next]);
    }, []);

    const removeAttachment = useCallback(
        (id) => {
            setPendingAttachments((prev) => {
                const index = prev.findIndex((p) => p.id === id);
                if (index < 0) return prev.filter((p) => p.id !== id);
                const target = prev[index];
                if (target?.preview) {
                    try {
                        URL.revokeObjectURL(target.preview);
                    } catch {
                        // ignore
                    }
                }
                let content = form.getFieldValue('description') || '';
                const linkRegex = new RegExp(`<a\\s(?=[^>]*href="#attachment-${index}")[^>]*>[\\s\\S]*?</a>`, 'gi');
                content = content.replace(linkRegex, '');
                for (let j = prev.length - 1; j > index; j--) {
                    content = content.replace(new RegExp(`#attachment-${j}`, 'g'), `#attachment-${j - 1}`);
                }
                form.setFieldValue('description', content);
                return prev.filter((p) => p.id !== id);
            });
        },
        [form],
    );

    const removePendingLink = useCallback((idOrHref) => {
        const key = String(idOrHref ?? '').trim();
        if (!key) return;
        setPendingLinks((prev) => {
            const match =
                prev.find((x) => String(x.id) === key) ??
                prev.find((x) => String(x.url) === key);
            const next = prev.filter((x) => String(x.id) !== key && String(x.url) !== key);
            const href = match?.url || (key.startsWith('http') ? key : null);
            if (href) {
                const esc = href.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const content = String(form.getFieldValue('description') || '');
                const re = new RegExp(`<a\\s(?=[^>]*href=["']${esc}["'])[^>]*>[\\s\\S]*?<\\/a>`, 'i');
                form.setFieldValue('description', content.replace(re, ''));
            }
            return next;
        });
    }, [form]);

    const handleEditorLinkRemoved = useCallback(
        (href) => {
            if (!href) return;
            removePendingLink(String(href));
        },
        [removePendingLink],
    );

    const watchedDesc = Form.useWatch('description', form);
    useEffect(() => {
        const html = watchedDesc;
        if (!html || typeof html !== 'string') return;
        setPendingLinks((prev) => prev.filter((x) => !x?.url || html.includes(String(x.url))));
    }, [watchedDesc]);

    const handleLinkRequest = useCallback(
        (insertLink) => {
            linkModalCallbackRef.current = insertLink;
            linkModalForm.resetFields();
            setLinkModalOpen(true);
        },
        [linkModalForm],
    );

    const handleLinkModalSubmit = useCallback(() => {
        linkModalForm.validateFields().then((values) => {
            const rawUrl = (values.url || '').trim();
            const title = (values.title || '').trim();
            const check = validateLinkUrlInput(rawUrl);
            if (!check.ok) {
                linkModalForm.setFields([
                    { name: 'url', errors: [check.message || 'Enter a valid URL (https://example.com)'] },
                ]);
                return;
            }
            const normalizedUrl = check.normalized;
            const displayTitle = title || normalizedUrl;
            const insertLink = linkModalCallbackRef.current;
            if (insertLink) insertLink({ url: normalizedUrl, title: displayTitle });
            setPendingLinks((prev) => [
                ...prev,
                { id: `issue-upgrade-link-${Date.now()}`, url: normalizedUrl, title: displayTitle },
            ]);
            setLinkModalOpen(false);
            linkModalCallbackRef.current = null;
        });
    }, [linkModalForm]);

    const handleSubmit = useCallback(() => {
        form.validateFields().then((values) => {
            const files = pendingAttachments.map((p) => p.file).filter(Boolean);
            const payload = editingItem
                ? buildEditIssuePayload(values, { files, links: pendingLinks, existingAttachments })
                : buildCreateIssuePayload(values, files, pendingLinks);
            if (!payload) {
                message.warning('Please complete required fields');
                return;
            }
            const onSuccessMsg = editingItem ? 'Updated successfully' : 'Created successfully';
            const onErrorMsg = editingItem ? 'Failed to update' : 'Failed to create';
            const submitAction = editingItem && getIssueOrUpgradeApiId(editingItem)
                ? (callbacks) => updateIssueOrUpgrade(getIssueOrUpgradeApiId(editingItem), payload, callbacks)
                : (callbacks) => createIssueOrUpgrade(payload, callbacks);
            submitAction({
                onSuccess: () => {
                    message.success(onSuccessMsg);
                    closeModal();
                },
                onError: (err) =>
                    message.error(err?.response?.data?.message || err?.message || onErrorMsg),
            });
        });
    }, [
        form,
        pendingAttachments,
        pendingLinks,
        editingItem,
        updateIssueOrUpgrade,
        createIssueOrUpgrade,
        closeModal,
    ]);

    return {
        canCreate,
        mentionUsers,
        form,
        modalOpen,
        openModal,
        openEditModal,
        closeModal,
        editingItem,
        handleSubmit,
        createPending,
        pendingAttachments,
        pendingLinks,
        existingAttachments,
        removePendingLink,
        handleFilesSelected,
        removeAttachment,
        linkModalOpen,
        setLinkModalOpen,
        linkModalForm,
        linkModalCallbackRef,
        handleLinkRequest,
        handleEditorLinkRemoved,
        handleLinkModalSubmit,
    };
}

function useProjectIssueUpgradeKanbanDrag({
    canSubmit,
    canResolve,
    canUpdate,
    onMove,
}) {
    const [dropTarget, setDropTarget] = useState(null);
    const [draggingColumn, setDraggingColumn] = useState(null);

    const clearDropTarget = useCallback(() => {
        setDropTarget(null);
        setDraggingColumn(null);
    }, []);

    const handleDndDragStart = useCallback((payload) => {
        setDraggingColumn(payload?.sourceColumn ?? null);
    }, []);

    const handleDndDragOverColumn = useCallback((columnId) => {
        setDropTarget(columnId);
    }, []);

    const canMoveToColumn = useCallback(
        (target) => {
            if (target === ISSUE_UPGRADE_COL.SUBMITTED) return canSubmit;
            if (target === ISSUE_UPGRADE_COL.RESOLVED) return canResolve;
            if (target === ISSUE_UPGRADE_COL.ACTIVE) return canUpdate;
            return false;
        },
        [canSubmit, canResolve, canUpdate],
    );

    const handleDndDragEnd = useCallback(
        ({ overColumnId, payload }) => {
            const id = payload?.id;
            const sourceColumn = payload?.sourceColumn ?? null;
            const targetColumn = overColumnId ?? null;
            clearDropTarget();
            if (!id || !sourceColumn || !targetColumn || sourceColumn === targetColumn) return;
            if (!canMoveToColumn(targetColumn)) {
                message.warning("You don't have permission for this move.");
                return;
            }
            onMove?.({
                id,
                sourceColumn,
                targetColumn,
                callbacks: {
                    onSuccess: () => message.success('Status updated'),
                    onError: (err) =>
                        message.error(err?.response?.data?.message || err?.message || 'Failed to update status'),
                },
            });
        },
        [clearDropTarget, canMoveToColumn, onMove],
    );

    return {
        dropTarget,
        draggingColumn,
        clearDropTarget,
        handleDndDragStart,
        handleDndDragOverColumn,
        handleDndDragEnd,
    };
}

export function getIssueOrUpgradeDisplayId(item) {
    const clientNum = item?.clientTicketNumber ?? item?.client_ticket_number;
    if (clientNum != null && String(clientNum).trim() !== '') {
        const cs = String(clientNum).trim();
        if (/^CT-/i.test(cs)) return cs;
        return `CT-${cs}`;
    }
    const raw =
        item?.issuesAndUpgradesNumber ??
        item?.issues_and_upgrades_number ??
        item?.issueOrUpgradeNumber ??
        item?.issue_or_upgrade_number ??
        item?.number ??
        item?.code ??
        getIssueOrUpgradeId(item);
    if (raw == null || raw === '') return 'IAU-—';
    const s = String(raw).trim();
    if (!s) return 'IAU-—';
    if (/^IAU-/i.test(s)) return s;
    return `IAU-${s}`;
}

export function getIssueOrUpgradeMarkdown(item) {
    const d = item?.description;
    const mentions = item?.descriptionMentions;
    if (d == null) return '';
    if (typeof d === 'string') return resolveDescriptionMentionsForDisplay(d.trim(), mentions);
    if (typeof d?.markdown === 'string') return resolveDescriptionMentionsForDisplay(d.markdown.trim(), mentions);
    if (typeof d?.content === 'string') return resolveDescriptionMentionsForDisplay(d.content.trim(), mentions);
    return resolveDescriptionMentionsForDisplay('', mentions) || '';
}

export function getIssueOrUpgradeType(item) {
    const t = String(item?.type ?? '').trim().toLowerCase();
    return t === 'upgrade' ? 'upgrade' : 'issue';
}

export function getIssueOrUpgradeApiId(item) {
    const id = getIssueOrUpgradeId(item);
    return id == null || id === '' ? undefined : String(id);
}

/** Tasks linked to an issue/upgrade (embedded on list/detail API responses). */
export function getIssueUpgradeLinkedTasks(item) {
    const raw =
        item?.tasks ??
        item?.Tasks ??
        item?.linkedTasks ??
        item?.linked_tasks ??
        item?.taskList ??
        [];
    return Array.isArray(raw) ? raw : [];
}

export function getIssueUpgradeLinkedTasksCount(item) {
    const list = getIssueUpgradeLinkedTasks(item);
    if (list.length > 0) return list.length;
    const n = item?.taskCount ?? item?.tasksCount ?? item?.linkedTaskCount ?? item?.linked_task_count;
    if (typeof n === 'number' && Number.isFinite(n) && n > 0) return Math.floor(n);
    if (typeof n === 'string' && /^\d+$/.test(n.trim())) return Number(n.trim());
    return 0;
}

export function formatIssueUpgradeDateTime(value) {
    if (!value) return '—';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '—';

    // Force English month name to keep UI consistent with the requested format.
    const date = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    }).format(d);

    // `09:00 AM` → `09 : 00 AM`
    const time = new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    })
        .format(d)
        .replace(':', ' : ');

    return `${date} | ${time}`;
}

function getNamedUserLabel(value, fallbackId) {
    const valueFullName = [value?.firstName, value?.lastName].filter(Boolean).join(' ').trim();
    const direct =
        typeof value === 'string'
            ? value
            : value?.name ??
              value?.fullName ??
              value?.displayName ??
              (valueFullName || value?.email);
    const named = String(direct ?? '').trim();
    if (named) return named;
    const fallbackFullName = [fallbackId?.firstName, fallbackId?.lastName].filter(Boolean).join(' ').trim();
    const fallbackRaw =
        typeof fallbackId === 'string'
            ? ''
            : fallbackId?.name ??
              fallbackId?.fullName ??
              fallbackId?.displayName ??
              (fallbackFullName || fallbackId?.email);
    const fallback = String(fallbackRaw ?? '').trim();
    return fallback || 'Unknow';
}

export function getIssueOrUpgradeActorName(item) {
    const submittedByName = getNamedUserLabel(
        item?.submittedByName ??
            item?.submittedByUserName ??
            item?.submittedByUser ??
            item?.submittedByDetails,
        item?.submittedBy,
    );
    if (submittedByName && submittedByName !== 'Unknow') return submittedByName;
    return getNamedUserLabel(
        item?.createdByName ??
            item?.createdByUserName ??
            item?.createdByUser ??
            item?.createdByDetails,
        item?.createdBy,
    );
}
